import { useMutation } from '@apollo/client';
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Caret from '../components/Caret';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import FileUploadMenuItem from '../components/FileUploadMenuItem';
import LoadingIndicator from '../components/LoadingIndicator';
import { MenuItem, MenuSeparator } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import { gql } from '../graphql/generated/gql';
import attachmentIcon from '../icons/non-prefilled/16x16/attachment.svg';
import googleDriveIcon from '../icons/non-prefilled/16x16/googleDrive.svg';
import importIcon from '../icons/non-prefilled/16x16/import.svg';
import CreatePostButton from '../post/CreatePostButton';
import CreatePostMenuItem from '../post/CreatePostMenuItem';
import GoogleDrivePicker from '../search/GoogleDrivePicker';
import TemplatesMenuItem from '../templates/TemplatesMenuItem';
import { UIActions } from '../ui/uiSlice';
import useNavigate from '../utils/router/useNavigate';
import postPath from '../utils/url/postPath';
import * as styles from './TopbarCreatePostButton.module.scss';

const IMPORT_FILES_MUTATION = gql(/** @complexity 8 */ `
  mutation createFileWithImport($files: [Upload]!, $topicId: ID) {
    importFiles(files: $files, isDraft: true, topicId: $topicId) {
      post {
        publishedAt
        ...postPath_post
      }
    }
  }
`);

interface TopbarCreatePostMenuContentProps {
  topicId?: string;
  onGoogleDriveImportClick(): void;
}

export const TopbarCreatePostMenuContent = ({
  topicId,
  onGoogleDriveImportClick,
}: TopbarCreatePostMenuContentProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(
    (state) => state.session.user.type === 'ADMIN',
  );

  const [importFiles] = useMutation(IMPORT_FILES_MUTATION);

  const handleUpload = (files: File[]) => {
    const dismissModal = () => dispatch(UIActions.closeModal());
    dispatch(
      UIActions.openModal(() => <LoadingIndicator delay={0} card />, {
        disableOutsideClose: true,
      }),
    );

    importFiles({ variables: { files, topicId } })
      .then(({ data }) => {
        if (data?.importFiles[0]) {
          const [{ post }] = data.importFiles;
          dismissModal();
          navigate(postPath(post));
        }
      })
      .catch(dismissModal);
  };

  return (
    <>
      <CreatePostMenuItem topicId={topicId}>Create new post</CreatePostMenuItem>
      <TemplatesMenuItem />
      <MenuSeparator />
      <MenuItem
        icon={attachmentIcon}
        subMenuItems={
          <>
            <FileUploadMenuItem accept=".md" onUpload={handleUpload}>
              Markdown
            </FileUploadMenuItem>
            <FileUploadMenuItem
              accept="text/html,.html,.htm"
              onUpload={handleUpload}
            >
              HTML
            </FileUploadMenuItem>
            <FileUploadMenuItem accept=".docx" onUpload={handleUpload}>
              Docx
            </FileUploadMenuItem>
          </>
        }
      >
        Import from file
      </MenuItem>
      {isAdmin && (
        <MenuItem
          icon={importIcon}
          subMenuItems={
            <>
              <MenuItem
                icon={
                  <ExternalBrandLogo brand="atlassian" product="confluence" />
                }
                to="/admin/import/confluence"
              >
                Confluence
              </MenuItem>
              <MenuItem icon={importIcon} to="/admin/import">
                Other sources
              </MenuItem>
            </>
          }
        >
          Bulk import
        </MenuItem>
      )}
      <MenuItem icon={googleDriveIcon} onClick={onGoogleDriveImportClick}>
        Import from Drive
      </MenuItem>
    </>
  );
};

interface TopbarCreatePostButtonProps {
  topicId?: string;
}

const TopbarCreatePostButton = ({ topicId }: TopbarCreatePostButtonProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const isLocked = useAppSelector(
    (state) => state.session.organization.isLocked,
  );

  const handleGoogleDriveImport = ({
    topicIds,
  }: {
    topicIds?: string[];
  } = {}) => {
    dispatch(
      UIActions.openModal(GoogleDrivePicker, {
        topicIds,
        disableOutsideClose: true,
      }),
    );
  };

  useEffect(() => {
    if (searchParams.get('importFrom') === 'google') {
      const topicIds = searchParams.getAll('topicIds');
      handleGoogleDriveImport({ topicIds });
      setSearchParams({});
    }
  });

  const dropdownButton = (
    <Button
      className={styles.dropdownButton}
      disabled={Boolean(isLocked)}
      variant="secondary"
      aria-label="More options"
    >
      <Caret direction="down" size={10} />
    </Button>
  );

  return (
    <div className={styles.container}>
      <CreatePostButton
        className={styles.mainButton}
        topicId={topicId}
        variant="secondary"
      >
        Create post
      </CreatePostButton>
      {isLocked ? (
        dropdownButton
      ) : (
        <OverflowMenu button={dropdownButton}>
          <TopbarCreatePostMenuContent
            topicId={topicId}
            onGoogleDriveImportClick={handleGoogleDriveImport}
          />
        </OverflowMenu>
      )}
    </div>
  );
};

export default TopbarCreatePostButton;
